class CartPopupElement extends HTMLElement {
  constructor() {
    super();
    this.hideTimer = null;
    this.duration = 5000; // auto hide after 5s
  }

  connectedCallback() {
    if (!this.querySelector(".cart-popup-wrap")) {
      this.innerHTML = `
        <div class="cart-popup-wrap" aria-live="polite" aria-atomic="true" hidden>
          <div class="cart-popup-header">
            <div class="cart-popup-header-title">JUST ADDED TO YOUR CART</div>
            <button class="cart-popup-close" aria-label="Close">&times;</button>
          </div>
          <div class="cart-popup-inner">
            <div class="cart-popup-media"></div>
            <div class="cart-popup-body">
              <div class="cart-popup-title"></div>
              <div class="cart-popup-qty"></div>
              <div class="cart-popup-actions">
                <a class="cart-popup-view-cart" href="/cart">View Cart</a>
                <button class="cart-popup-continue" type="button">
                  <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                  </svg>
                  <span>Continue shopping</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    this.wrap = this.querySelector(".cart-popup-wrap");
    this.media = this.querySelector(".cart-popup-media");
    this.titleEl = this.querySelector(".cart-popup-title");
    this.qtyEl = this.querySelector(".cart-popup-qty");
    this.viewCart = this.querySelector(".cart-popup-view-cart");
    this.continueBtn = this.querySelector(".cart-popup-continue");
    this.closeBtn = this.querySelector(".cart-popup-close");

    this.closeBtn.addEventListener("click", this.hide.bind(this));
    this.continueBtn.addEventListener("click", (e) => {
      e.preventDefault();
      window.location.href = "/collections/all";
    });

    // Pause auto-hide while hovered/focused
    this.wrap.addEventListener("mouseenter", () => {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    });
    this.wrap.addEventListener("mouseleave", () => {
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), this.duration);
    });
    this.wrap.addEventListener("focusin", () => {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    });
    this.wrap.addEventListener("focusout", () => {
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), this.duration);
    });

    this.viewCart.addEventListener("click", () => {
      this.hide();
    });
  }

  show(response) {
    // Accept either a simple payload { title, quantity, image } or full ajax response
    let title = "";
    let qty = null;
    let image = null;

    try {
      // If response contains title directly, prefer it
      if (response && typeof response === "object") {
        title = response.title || response.product_title || response.name || "";
        qty = response.quantity ?? response.qty ?? response.quantity_added ?? null;
        image = response.image || response.image_url || response.image_src || null;
        // If response contains items array, use first item
        if ((!title || title === "") && Array.isArray(response.items) && response.items.length) {
          const item = response.items[0];
          title = title || item.title || item.product_title || item.name || "";
          qty = qty ?? (item.quantity ?? item.qty ?? item.quantity_added ?? null);
          image = image || item.image || item.image_src || item.featured_image || null;
        }
        // If sections are present, try to parse HTML for title/image
        if ((!title || title === "") && response.sections) {
          const parser = new DOMParser();
          const selectors = [
            ".cart-item__title",
            ".cart-item-title",
            ".mini-cart-item-title",
            ".js-cart-item .cart-item__title",
            ".js-cart-item .cart-item-title",
            ".mini-cart-item .product-title",
            ".product-title",
            ".mini-cart-item .title",
            ".cart-item .title",
            "a[href*=\"/products/\"]",
            ".line-item__title",
          ];
          for (const key of Object.keys(response.sections)) {
            const html = response.sections[key];
            if (!html) continue;
            const doc = parser.parseFromString(html, "text/html");
            for (const sel of selectors) {
              const el = doc.querySelector(sel);
              if (el && el.textContent.trim()) {
                title = el.textContent.trim();
                break;
              }
            }
            if (title) break;
          }
        }
      }
    } catch (e) {
      // ignore parse errors
    }

    // Fallback: read page DOM for product title if still missing
    if (!title || title.trim() === "") {
      title =
        document.querySelector(".product-single__title")?.textContent ||
        document.querySelector(".product__title")?.textContent ||
        document.querySelector(".product-title")?.textContent ||
        document.querySelector("#ProductTitle")?.textContent ||
        document.querySelector("h1[itemprop='name']")?.textContent ||
        document.querySelector("h1")?.textContent ||
        "";
    }

    title = title ? title.trim() : "Added to cart";

    // Render image
    if (image) {
      this.media.innerHTML = `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" />`;
    } else {
      this.media.innerHTML = `<div class="cart-popup-media-placeholder"></div>`;
    }

    this.titleEl.textContent = title;
    this.qtyEl.textContent = qty ? `QTY: ${qty}` : "";

    // cart count if provided
    const cartCount =
      response?.cart?.item_count ?? response?.item_count ?? (response?.items ? response.items.length : null);
    this.viewCart.textContent = cartCount ? `View Cart (${cartCount})` : "View Cart";

    this.viewCart.setAttribute("href", "/cart");

    // show popup
    this.wrap.hidden = false;
    this.wrap.classList.add("is-visible");
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.hide(), this.duration);
  }

  hide() {
    if (!this.wrap) return;
    this.wrap.classList.remove("is-visible");
    this.wrap.hidden = true;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

if (!customElements.get("cart-popup")) {
  customElements.define("cart-popup", CartPopupElement);
}

